import {createContext, memo, useMemo} from 'react'

import type {TableDataType} from '../../../../components/table/table-data-type'
import type {ColumnModel} from '../../../../models/column-model'
import {useAllColumns} from '../../../../state-providers/columns/use-all-columns'
import {useColumnWidth} from '../../../../state-providers/columns/use-column-width'
import type {ColumnDef} from '../../helpers/types'
import {buildFieldColumnDef} from './build-field-column-def'

type TableColumns = {
  /** Column definitions for use with @tanstack/react-table */
  columnDefinitions: Array<ColumnDef<TableDataType>>

  /**
   * Record of column visibility used by tanstack/react-table [docs](https://tanstack.com/table/v8/docs/api/features/column-visibility)
   */
  columnVisibility: Record<string, boolean>
}

export const TableColumnsContext = createContext<TableColumns | null>(null)

export const TableColumnsProvider = memo<{
  children: React.ReactNode
  isFieldVisible: (column: ColumnModel) => boolean
}>(function TableColumnsProvider({isFieldVisible, children}) {
  const {allColumns} = useAllColumns()
  const {getWidth} = useColumnWidth()

  const columnDefinitions: Array<ColumnDef<TableDataType>> = useMemo(() => {
    return allColumns.map(column => buildFieldColumnDef(column, getWidth(column.id)))
  }, [allColumns, getWidth])

  // For the roadmap view, "VisibleFieldsProvider" doesn't currently control the columns shown in a view.
  const columnVisibility = useMemo(() => {
    return allColumns.reduce(
      (acc, col) => {
        acc[col.id.toString()] = isFieldVisible(col)
        return acc
      },
      {} as Record<string, boolean>,
    )
  }, [allColumns, isFieldVisible])

  const contextValue = useMemo(() => {
    return {columnDefinitions, columnVisibility}
  }, [columnDefinitions, columnVisibility])

  return <TableColumnsContext value={contextValue}>{children}</TableColumnsContext>
})

try{ TableColumnsContext.displayName ||= 'TableColumnsContext' } catch {}
try{ TableColumnsProvider.displayName ||= 'TableColumnsProvider' } catch {}